import { UI_PRIMITIVE__slider_range } from '@/components/ui/slider/UI_PRIMITIVE__slider_range';
import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_effect } from '@/hooks/_base/use_effect';
import { use_slice_generation, use_slice_ui } from '@/hooks/store/use_slices';
import { UTILS__graph_data } from '@/utils/UTILS__graph_data';
import { assert_equal } from '@shared/asserts/utils_asserts';
import { FIELD_PRIMITIVE__base_number } from '@shared/dto/generation/graph_invoke/_fields/_primitives/number/FIELD_PRIMITIVE__base_number';
import { DTO__number_range } from '@shared/dto/generation/settings/DTO__number_range';
import { useMemo, useState } from 'react';

interface PROPS {
  field: FIELD_PRIMITIVE__base_number;
}

export function G_UI_FIELD_PRIMITIVE__number_slider_range({ field }: PROPS) {
  const dispatch = use_dispatch();

  const { generation_current } = use_slice_generation(['generation_current']);
  const { g_general__ui_disabled, g_hovered_grid2d_column, g_hovered_grid2d_row } = use_slice_ui([
    'g_general__ui_disabled',
    'g_hovered_grid2d_column',
    'g_hovered_grid2d_row',
  ]);
  const value_hovered = useMemo(() => get_value_hovered(), [g_hovered_grid2d_column, g_hovered_grid2d_row, field]);

  /*
   * STATE
   */
  const [value, set_value] = useState(field.get_value_range());

  /*
   *
   */
  use_effect(() => set_value(field.get_value_range()), [field]);

  /*
   * EVENTS
   */
  function onValueChange(values: number[]) {
    assert_equal(values.length, 2);
    set_value(new DTO__number_range(values[0], values[1]));
  }

  async function onValueCommit(values: number[]) {
    assert_equal(values.length, 2);
    let value_range = new DTO__number_range(values[0], values[1]);

    set_value(value_range);

    if (field.get_value_range().is_equal(value_range)) return;

    let { graph } = generation_current.tool;

    assert_equal(field.meta.graph_id, graph.meta.graph_id);

    let value = value_range.clamp(field.get_value());

    let field_new = field.clone({ value_range, value });

    await UTILS__graph_data.update_field(graph, field_new);
  }

  /*
   * METHODS
   */
  function is_disabled() {
    return g_general__ui_disabled || field.meta.ui__is_readonly;
  }

  function get_value_hovered() {
    let out = NaN;

    if (field.meta.grid2d__is_column) {
      out = field.try_get_value_from_hovered_grid2d_dimension(g_hovered_grid2d_column);
      out = out.to_fixed(2);
    } else if (field.meta.grid2d__is_row) {
      out = field.try_get_value_from_hovered_grid2d_dimension(g_hovered_grid2d_row);
      out = out.to_fixed(2);
    }

    return out;
  }

  /*
   * HTML
   */

  return (
    <UI_PRIMITIVE__slider_range
      {...{ value_hovered }}
      min={field.get_values_allowed().min}
      max={field.get_values_allowed().max}
      disabled={is_disabled()}
      value_used={field.get_value().to_fixed(2)}
      step={field.slider_step}
      value={[value.min, value.max]}
      defaultValue={[field.get_value_range().min, field.get_value_range().max]}
      {...{ onValueChange, onValueCommit }}
      className="w-full"
    />
  );
}
